// src/Transactions.jsx
import { useEffect, useState, useRef } from "react";
import React from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc, 
  doc,
  updateDoc,
  addDoc,
  onSnapshot,
  serverTimestamp
} from "firebase/firestore";
import { db } from "./firebase";
import { auth } from "./firebase";
import PlaidLinkButton from "./PlaidLinkButton";
import { useNavigate } from "react-router-dom";
import {
  fetchUserRules,
  matchTransactionToRule,
  saveUserRule,
} from "./ruleMatcher";
import { getSuggestedCategory } from "./getSuggestedCategory";
import CategoryEditor from "./CategoryEditor";
import RuleCreator from "./RuleCreator";
import RuleConfirmationModal from "./RuleConfirmationModal";
import TransactionRowStyled from "./TransactionRowStyled";
import UploadTransactions from "./UploadTransactions";
import DeleteMockTransactionsButton from "./DeleteMockTransactionsButton";
import RuleUpdateModal from "./RuleUpdateModal";
import { writeBatch } from "firebase/firestore";
import { deleteDoc } from "firebase/firestore";
import MiniCategoryModal from "./MiniCategoryModal";
import { useSearchParams } from "react-router-dom";
import RuleConflictModal from "./RuleConflictModal";

let globalScrollToTxId = null;

// Coerce any value we render in <option> to a safe string
const toLabel = (v) => {
  if (typeof v === "string") return v;
  if (v == null) return "";
  if (typeof v === "object") {
    if (typeof v.name === "string") return v.name;
    if (typeof v.label === "string") return v.label;
    if (typeof v.value === "string") return v.value;
    try { return String(v); } catch { return ""; }
  }
  return String(v);
};

// --- Date helpers: display in a TZ-safe way and compare in filters ---
const formatTxDate = (tx) => {
  try {
    // Prefer an original string if you saved one with the doc
    const s = tx.dateString || tx.plaid_date || tx.date_str;
    if (typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split("-");
      return `${m}/${d}/${y}`; // US-style mm/dd/yyyy to match your UI
    }
    // Firestore Timestamp → format using UTC to avoid back-dating
    if (tx.date?.seconds) {
      return new Date(tx.date.seconds * 1000)
        .toLocaleDateString(undefined, { timeZone: "UTC" });
    }
    // Raw JS Date (fallback)
    if (tx.date instanceof Date) {
      return tx.date.toLocaleDateString(undefined, { timeZone: "UTC" });
    }
  } catch {}
  return "N/A";
};

const txDateToMillis = (tx) => {
  // If the doc stores an ISO-like string date
  if (typeof tx.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(tx.date)) {
    return Date.parse(tx.date + "T00:00:00Z");
  }
  // Firestore Timestamp
  if (tx.date?.seconds) return tx.date.seconds * 1000;
  // Raw JS Date
  if (tx.date instanceof Date) return tx.date.getTime();
  return null;
};

// Extract the Plaid transaction_id for tombstones.
// Your root docs are in collection("transactions") and the docId is often `${uid}_${plaidId}`.
// We also support multiple field names, plus fallback parsing from doc id.
const getPlaidTxIdFromTx = (tx) => {
  if (!tx) return null;

  // Common field names you may have on root docs
  const direct =
    tx.plaid_tx_id ||
    tx.plaidTxId ||
    tx.transaction_id ||
    tx.transactionId ||
    tx.plaid_transaction_id ||
    tx.plaid_transactionId;

  if (direct) return String(direct);

  // Fallback: parse from root doc id like `${uid}_${plaidTxId}`
  try {
    const user = auth.currentUser;
    if (user?.uid && typeof tx.id === "string" && tx.id.startsWith(user.uid + "_")) {
      return tx.id.slice(user.uid.length + 1);
    }
  } catch {}

  return null;
};

// Local delete ONLY for manual/non-Plaid transactions (no plaid_tx_id)
const deleteTransactionDoc = async (transactionId) => {
  const ref = doc(db, "transactions", transactionId);
  await deleteDoc(ref);
};

// Ensure categories/subcategories are strings before matching
const normalizeBudgetsForMatching = (list = []) =>
  list.map((b) => {
    const cleanCategory = toLabel(b?.category ?? "");
    const cleanSubs = (b?.subcategories || []).map((s) => {
      const name = toLabel(typeof s === "string" ? s : (s?.name ?? s));
      return typeof s === "object" ? { ...s, name } : { name };
    });
    return { ...b, category: cleanCategory, subcategories: cleanSubs };
  });

const normalizeName = (s) => toLabel(s).trim().toLowerCase();

// Treat legacy placeholder values as invalid (no migration, UI-only)
const isPlaceholderSubcategory = (v) =>
  toLabel(v).trim().toLowerCase() === "subcategory";

// Is this category/subcategory pair currently selectable in the user's budgets?
const isActiveBudgetOption = (category, subcategory, budgetsList = []) => {
  const cat = toLabel(category).trim();
  const sub = toLabel(subcategory).trim();

  if (!cat || !sub) return false;
  if (cat.toLowerCase() === "none" || sub.toLowerCase() === "none") return false;
  if (isPlaceholderSubcategory(sub)) return false;

  return budgetsList.some(
    (b) =>
      toLabel(b?.category).trim() === cat &&
      Array.isArray(b?.subcategories) &&
      b.subcategories.some((s) => toLabel(s?.name ?? s).trim() === sub)
  );
};

// Resolve what the user is trying to confirm (prefer matched/UI, then suggested)
const resolveChosenPair = (tx, budgetsList = []) => {
  const matchedCategory = toLabel(tx?.matchedCategory ?? tx?.categoryMatch?.category ?? "").trim();
  const matchedSubRaw = tx?.matchedSubcategory ?? tx?.categoryMatch?.subcategory ?? "";
  const matchedSubcategory = isPlaceholderSubcategory(matchedSubRaw)
    ? ""
    : toLabel(matchedSubRaw).trim();

  const suggestedCategory = toLabel(tx?.suggestedCategory ?? "").trim();
  const suggestedSubRaw = tx?.suggestedSubcategory ?? "";
  const suggestedSubcategory = isPlaceholderSubcategory(suggestedSubRaw)
    ? ""
    : toLabel(suggestedSubRaw).trim();

  // Prefer matched only if it is valid + active
  if (isActiveBudgetOption(matchedCategory, matchedSubcategory, budgetsList)) {
    return { category: matchedCategory, subcategory: matchedSubcategory };
  }

  // Otherwise, fall back to suggested only if it is valid + active
  if (isActiveBudgetOption(suggestedCategory, suggestedSubcategory, budgetsList)) {
    return { category: suggestedCategory, subcategory: suggestedSubcategory };
  }

  // Otherwise, return whatever is present so callers can hard-stop
  return {
    category: matchedCategory || suggestedCategory || "",
    subcategory: matchedSubcategory || suggestedSubcategory || "",
  };
};

// --- Call Cloud Functions (requires Authorization: Bearer <idToken>) ---
async function authedPost(path, body) {
  const user = auth.currentUser;
  if (!user) throw new Error("Not logged in");
  const idToken = await user.getIdToken();

  // If you have a full functions URL in env, use it. Otherwise rely on Hosting rewrites.
  const base = import.meta?.env?.VITE_FUNCTIONS_BASE_URL || "";
  const url = `${base}${path}`;

  const r = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body || {}),
  });

  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json?.error || `Request failed (${r.status})`);
  return json;
}

const hasTransfersBucket = (list = []) =>
  list.some((b) => normalizeName(b.category) === "transfers & payments (not in budget)");

const TRELLI_GREEN = "#00B074";
const TRELLI_YELLOW = "#F4C542";
const BTN_RADIUS = 12; // match your other Trelli buttons

// ─────────────────── Responsive helper ───────────────────
const MOBILE_BREAKPOINT = 768;
function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.matchMedia(`(max-width: ${breakpoint}px)`).matches
      : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    try { mq.addEventListener("change", handler); } catch {
      // Safari
      mq.addListener(handler);
    }
    return () => {
      try { mq.removeEventListener("change", handler); } catch {
        mq.removeListener(handler);
      }
    };
  }, [breakpoint]);

  return isMobile;
}

export default function Transactions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [rules, setRules] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRuleModal, setShowRuleModal] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(null);
  const [filterConfirmOnly, setFilterConfirmOnly] = useState(false);
  const [filterNeedsAttention, setFilterNeedsAttention] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSubcategory, setFilterSubcategory] = useState("");
  const [filterAccount, setFilterAccount] = useState("");
  const [filterDateRange, setFilterDateRange] = useState({ start: "", end: "" });
  const [filterAmount, setFilterAmount] = useState("");
  const [filterType, setFilterType] = useState(""); // income or expense
  const isMobile = useIsMobile();
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [uid, setUid] = useState(auth.currentUser?.uid || null);
  // Map Plaid accountId -> friendly display name (e.g., "Chase Sapphire • 1234")
  const [accountNameById, setAccountNameById] = useState({});

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUid(u?.uid || null));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!uid) {
      setAccountNameById({});
      return;
    }

    const ref = collection(db, "users", uid, "plaid", "accounts", "byId");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const next = {};
        snap.forEach((docSnap) => {
          const d = docSnap.data() || {};
          const name =
            d.name ||
            d.official_name ||
            d.officialName ||
            "";
          const mask = d.mask ? String(d.mask) : "";
          const label =
            name && mask ? `${name} • ${mask}` :
            name ? name :
            mask ? mask :
            docSnap.id;

          next[docSnap.id] = label;
        });
        setAccountNameById(next);
      },
      (err) => {
        console.error("[Transactions] accounts/byId subscription failed:", err);
        setAccountNameById({});
      }
    );

    return () => unsub();
  }, [uid]);

  const uniqueAccounts = Array.from(new Set(transactions.map((tx) => tx.accountId).filter(Boolean)));
  // Count how many filters are currently active (for the yellow badges)
  const activeFilterCount = React.useMemo(() => {
    let c = 0;
    if (filterCategory) c++;
    if (filterSubcategory) c++;
    if (filterAccount) c++;
    if (filterType) c++;
    if (filterStatus !== "all") c++;
    if (filterDateRange.start) c++;
    if (filterDateRange.end) c++;
    if (filterAmount) c++;
    if (filterConfirmOnly) c++;
    if (filterNeedsAttention) c++;
    return c;
  }, [
    filterCategory,
    filterSubcategory,
    filterAccount,
    filterType,
    filterStatus,
    filterDateRange.start,
    filterDateRange.end,
    filterAmount,
    filterConfirmOnly,
    filterNeedsAttention
  ]);
    // Single-column amounts layout for laptops
  const headerLabels = isMobile
    ? ["Vendor", "Amount"] // not used for mobile header row; safe placeholder
    : ["", "Vendor", "Amount", "Date", "Category", "Subcategory", "Memo", "Status", "Actions"];

  // Match the row widths used below (sum ≈ 1204px now with checkbox col)
  const headerWidths = isMobile
    ? ["auto", "auto"]
    : [
        "44px",  // Checkbox
        "130px", // Vendor
        "135px", // Amount
        "100px", // Date
        "180px", // Category
        "155px", // Subcategory
        "210px", // Memo
        "130px", // Status
        "120px"  // Actions
      ];

  const [page, setPage] = useState(1);
  const transactionsPerPage = 50;
  const [draftChanges, setDraftChanges] = useState({});
  const [showUpdateModal, setShowUpdateModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [showReassignModal, setShowReassignModal] = useState(null);
  const [isReassignSaving, setIsReassignSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addVendor, setAddVendor] = useState("");
  const [addDate, setAddDate] = useState(""); // yyyy-mm-dd
  const [addAmount, setAddAmount] = useState("");
  const [addType, setAddType] = useState("expense"); // "expense" | "income"
  const [addCategory, setAddCategory] = useState("");
  const [addSubcategory, setAddSubcategory] = useState("");

  // ───────────────── Bulk select (desktop-only) ─────────────────
  const [selectedTxIds, setSelectedTxIds] = useState(() => new Set());
  const [bulkCategory, setBulkCategory] = useState("");
  const [bulkSubcategory, setBulkSubcategory] = useState("");

  const masterCheckboxRef = useRef(null);

  const isSelected = (id) => selectedTxIds.has(id);

  const toggleOneSelected = (id) => {
    setSelectedTxIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearBulkSelection = () => {
    setSelectedTxIds(new Set());
    setBulkCategory("");
    setBulkSubcategory("");
  };

  // Derived subcategory options
  const addSubcategoryOptions = (budgets.find(b => toLabel(b.category) === addCategory)?.subcategories || [])
    .map(s => toLabel(typeof s === "string" ? s : (s?.name ?? s)));


  const loadRules = async () => {
  const fetchedRules = await fetchUserRules();
    setRules(fetchedRules || []);
  };
  const fullyRefreshEverything = async () => {
    console.log("🔄 Starting full refresh...");
    setIsRefreshing(true);
    try {
      await refreshBudgets();
      console.log("✅ Budgets refreshed");
      await loadRules();
      console.log("✅ Rules refreshed");
      await fetchTransactions();
      console.log("✅ Transactions refreshed");
    } catch (err) {
      console.error("❌ Error during full refresh:", err);
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
        console.log("✅ Done refreshing UI");
      }, 100);
    }
  };
  const [scrollToTxId, setScrollToTxId] = useState(null);
  const refsById = useRef({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [showMiniModal, setShowMiniModal] = useState(false);
  const [miniModalMode, setMiniModalMode] = useState(null);
  const [defaultMiniCategory, setDefaultMiniCategory] = useState("");

  // One line height for all toolbar buttons
  const toolbarBtnStyle = {
    height: 40,
    padding: "0 14px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    fontWeight: 600,
    borderRadius: BTN_RADIUS,
    border: "none",
    cursor: "pointer",
    background: TRELLI_GREEN,
    color: "#fff",
  };

  const fetchTransactions = async () => {
    console.log("📦 Fetching transactions...");
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) return;

      const q = query(
        collection(db, "transactions"),
        where("userId", "==", user.uid),
        orderBy("date", "desc")
      );
      const querySnapshot = await getDocs(q);

      const fetched = await Promise.all(
        querySnapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const matchedRule = matchTransactionToRule(data, rules);
          const safeBudgets = normalizeBudgetsForMatching(budgets);
          const suggested = !matchedRule ? await getSuggestedCategory(data, safeBudgets) : null;

          if (!data.categoryMatch && matchedRule?.category && matchedRule?.subcategory) {
            const txRef = doc(db, "transactions", docSnap.id);
            await updateDoc(txRef, {
              categoryMatch: {
                type: "rule",
                category: matchedRule.category,
                subcategory: matchedRule.subcategory,
                source: "rule"
              },
              category: matchedRule.category,
              subcategory: matchedRule.subcategory,
              needsReview: false,
              // ✅ top-level for reporting
              status: "confirmed",
              type: "rule",
              confirmedAt: serverTimestamp(),
            });
          }

          let matchedCategory = data.categoryMatch?.category || matchedRule?.category || null;
          let matchedSubcategory = data.categoryMatch?.subcategory || matchedRule?.subcategory || null;
          const matchedSource = data.categoryMatch?.source || matchedRule?.source || null;

          // Lazy cleanup: treat legacy placeholder "subcategory" as invalid (UI behaves like blank)
          if (isPlaceholderSubcategory(matchedSubcategory)) {
            matchedSubcategory = null;
          }

          const suggestedCategory = !matchedRule && !matchedCategory ? suggested?.category || null : null;

          let suggestedSubcategory = !matchedRule && !matchedCategory ? suggested?.subcategory || null : null;
          if (isPlaceholderSubcategory(suggestedSubcategory)) {
            suggestedSubcategory = null;
          }

          const hasCategoryInBudget = matchedCategory && matchedSubcategory && budgets.some((b) =>
            b.category === matchedCategory && b.subcategories?.some((s) => s.name === matchedSubcategory)
          );

          const hasAmountInBudget = matchedCategory && matchedSubcategory && budgets.some((b) =>
            b.category === matchedCategory && b.subcategories?.some((s) => s.name === matchedSubcategory && parseFloat(s.amount || "0") > 0)
          );

          let reviewMessage = null;

          if (!matchedCategory) {
            reviewMessage = "⚠️ No category match found.";
          } else if (!matchedSubcategory) {
            // Covers blank OR placeholder-cleaned values
            reviewMessage = "⚠️ Please select a valid subcategory.";
          } else if (!hasCategoryInBudget) {
            if (matchedSource === "user") {
              reviewMessage = "⚠️ Rule match not found in current budget. Please edit this rule.";
            } else {
              reviewMessage = "⚠️ Category not found in your budget.";
            }
          } else if (!hasAmountInBudget) {
            reviewMessage = "⚠️ Category has no budget amount.";
          }

          return {
            id: docSnap.id,
            ...data,
            accountName: data.accountName || data.account_name || "",
            bankCategory: Array.isArray(data.category) ? data.category[0] : data.bankCategory || "",
            matchedCategory,
            matchedSubcategory,
            matchedSource,
            suggestedCategory,
            suggestedSubcategory,
            matchCriteria: suggested?.matchCriteria || null,
            needsReview: !!reviewMessage,
            reviewMessage,
            suggestedAmount: data.amount,
            suggestedPlaidCategory: (data.category || [])[0] || "",
          };
        })
      );
      setTransactions(fetched);
      console.log("📦 Transactions set:", fetched.length);
    } catch (err) {
      console.error("❌ Error fetching transactions:", err);
    } finally {
      setLoading(false);
      console.log("📦 Done fetching");
    }
    if (!hasLoadedOnce) {
      setHasLoadedOnce(true);
    }
  };

  const refreshBudgets = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const q = query(collection(db, "budgets"), where("userId", "==", user.uid));
    const snapshot = await getDocs(q);
    const fetched = snapshot.docs.map((doc) => doc.data());

    setBudgets(fetched);
  };

  const columnStyles = [
    // [0] spacer (unused by row, keep for index parity)
    { minWidth: "10px", textAlign: "center", flex: "0 0 10px", maxWidth: "10px" },

    // [1] Vendor
    { textAlign: "left", paddingLeft: "8px",
      flex: "0 0 130px", minWidth: "130px", maxWidth: "130px" },

    // [2] Amount (single column: green + for income, red – for expense)
    { textAlign: "center",
      flex: "0 0 135px", minWidth: "135px", maxWidth: "135px" },

    // [3] (placeholder to preserve indexes when singleAmount=true)
    { textAlign: "center",
      flex: "0 0 0px", minWidth: "0px", maxWidth: "0px", padding: 0 },

    // [4] Date
    { textAlign: "center",
      flex: "0 0 100px", minWidth: "100px", maxWidth: "100px" },

    // [5] Category
    { textAlign: "center",
      flex: "0 0 180px", minWidth: "180px", maxWidth: "180px" },

    // [6] Subcategory
    { textAlign: "center",
      flex: "0 0 155px", minWidth: "155px", maxWidth: "155px" },

    // [7] Memo
    { textAlign: "left", paddingLeft: "8px",
      flex: "0 0 210px", minWidth: "210px", maxWidth: "210px" },

    // [8] Status
    { textAlign: "center",
      flex: "0 0 130px", minWidth: "130px", maxWidth: "130px" },

    // [9] Actions
    { textAlign: "center",
      flex: "0 0 120px", minWidth: "120px", maxWidth: "120px" }
  ];

  const handleMemoChange = async (id, newMemo) => {
    const ref = doc(db, "transactions", id);
    await updateDoc(ref, { memo: newMemo });
    setTransactions((prev) =>
      prev.map((tx) => tx.id === id ? { ...tx, memo: newMemo } : tx)
    );
  };

  const saveManualTransaction = async () => {
    const user = auth.currentUser;
    if (!user) return;

    // Basic validation
    if (!addVendor.trim()) {
      alert("Please enter a vendor.");
      return;
    }
    if (!addDate) {
      alert("Please choose a date.");
      return;
    }
    if (!addAmount || isNaN(Number(addAmount))) {
      alert("Please enter a numeric amount.");
      return;
    }
    if (!addCategory || !addSubcategory) {
      alert("Please choose a category and subcategory.");
      return;
    }

    // Sign the amount: negative for expense, positive for income
    const signedAmount = addType === "expense" ? -Math.abs(Number(addAmount)) : Math.abs(Number(addAmount));

    // Firestore expects JS Date (auto-converted to Timestamp)
    const dateObj = new Date(addDate + "T12:00:00"); // noon to avoid TZ edge cases

    // Shape matches your existing schema:
    // userId, vendor, amount, date, status, source, accountId, and a categoryMatch
    const docData = {
      userId: user.uid,
      vendor: addVendor.trim(),
      amount: signedAmount,
      date: dateObj,
      source: "manual",
      accountId: "",
      categoryMatch: {
        type: "manual",
        category: addCategory,
        subcategory: addSubcategory,
        source: "user",
      },
      category: addCategory,
      subcategory: addSubcategory,
      needsReview: false,
      // ✅ top-level for reporting
      status: "confirmed",
      type: "manual",
      confirmedAt: serverTimestamp(),
      createdAt: new Date(),
    };

    await addDoc(collection(db, "transactions"), docData);

    // Reset form + close modal
    setAddVendor("");
    setAddDate("");
    setAddAmount("");
    setAddType("expense");
    setAddCategory("");
    setAddSubcategory("");
    setShowAddModal(false);

    // Refresh the grid so it appears immediately
    await fullyRefreshEverything();
  };

  const handleDraftCategoryChange = (id, newCategory) => {
    setDraftChanges(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        draftCategory: newCategory
      }
    }));
  };

  const handleDraftSubcategoryChange = (id, newSubcategory) => {
    setDraftChanges((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        draftSubcategory: newSubcategory,
      },
    }));
  };

  const handleCancelDraftChange = (id) => {
    setDraftChanges((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleConfirmDraftChange = async (id) => {
    const user = auth.currentUser;
    if (!user) return;

    const draft = draftChanges[id] || {};
    const tx = transactions.find((t) => t.id === id);
    if (!tx) return;

    const categoryToUse = toLabel(
      draft.draftCategory ?? tx.matchedCategory ?? tx.suggestedCategory ?? ""
    ).trim();

    const subcategoryToUse = toLabel(
      draft.draftSubcategory ?? tx.matchedSubcategory ?? tx.suggestedSubcategory ?? ""
    ).trim();

    // Don’t confirm incomplete selections
    if (!categoryToUse || !subcategoryToUse || isPlaceholderSubcategory(subcategoryToUse)) {
      setShowUpdateModal({
        title: "Selection incomplete",
        message: "Pick a valid category and subcategory before confirming.",
      });
      return;
    }

    // ✅ Commit to Firestore only on Confirm
    const ref = doc(db, "transactions", id);
    await updateDoc(ref, {
      categoryMatch: {
        type: "manual",
        category: categoryToUse,
        subcategory: subcategoryToUse,
        source: "user",
      },
      category: categoryToUse,
      subcategory: subcategoryToUse,
      needsReview: false,
      status: "confirmed",
      type: "manual",
      confirmedAt: serverTimestamp(),
    });

    // ✅ Pseudo rule block (keep your existing logic)
    const q = query(
      collection(db, "pseudoRules"),
      where("userId", "==", user.uid),
      where("vendor", "==", tx.vendor || ""),
      where(
        "plaidCategory",
        "==",
        Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || ""
      )
    );

    const snapshot = await getDocs(q);

    let updated = false;
    snapshot.forEach((docSnap) => {
      const pr = docSnap.data();
      const amountDiff =
        Math.abs(Number(pr.amount) - Number(tx.amount)) / Math.abs(Number(pr.amount) || 1);

      if (amountDiff < 0.01) {
        updateDoc(doc(db, "pseudoRules", docSnap.id), {
          category: categoryToUse,
          subcategory: subcategoryToUse,
          updatedAt: serverTimestamp(),
        });
        updated = true;
      }
    });

    if (!updated) {
      await addDoc(collection(db, "pseudoRules"), {
        userId: user.uid,
        vendor: tx.vendor || "",
        plaidCategory: Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || "",
        amount: tx.amount,
        category: categoryToUse,
        subcategory: subcategoryToUse,
        createdAt: serverTimestamp(),
      });
    }

    // Clear draft + refresh
    handleCancelDraftChange(id);
    await fullyRefreshEverything();
  };

  const [showUpload, setShowUpload] = useState(false);

  // Are there any linked Plaid accounts for this user?
  const [accountsLinked, setAccountsLinked] = useState(null); // null=loading, true/false once known

  // 🧭 Plaid connection status (for CTA & warning)
  const [plaidConn, setPlaidConn] = useState({
    hasItem: null,           // null while loading; true/false once known
    status: "unknown",       // "ok" | "error" | "none" | "unknown"
    errorCode: null,         // e.g., "ITEM_LOGIN_REQUIRED"
    updatedAt: null,
  });

  const checkForRuleConflict = async ({ vendor, plaidCategory, amount }) => {
    const user = auth.currentUser;
    if (!user) return null;

    const qBase = [
      where("userId", "==", user.uid),
      where("vendor", "==", vendor?.toLowerCase() || ""),
      where("plaidCategory", "==", plaidCategory?.toLowerCase() || "")
    ];

    const q = amount
      ? query(collection(db, "rules"), ...qBase, where("amount", "==", Number(amount)))
      : query(collection(db, "rules"), ...qBase);

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };


  useEffect(() => {
    loadRules();
  }, []);

  useEffect(() => {
    // DEBUG ONLY — temporary
    window.__trelliTestDeleteBulk = async () => {
      const sample = "4xk7365V6qtqLj9vX9wySX7wxMZvZVtJnnbY5"; // real plaid tx id
      return authedPost("/deleteTransactionsBulk", {
        plaid_tx_ids: [sample],
        source: "debug_console",
      });
    };

    console.log("Ready: run window.__trelliTestDeleteBulk() in console");
  }, []);

  // 🔎 Load Plaid link state for this user (used to show CTA / error box)
  useEffect(() => {
    const run = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const itemRef  = doc(db, "users", user.uid, "plaid", "item");
        const stateRef = doc(db, "users", user.uid, "plaid", "state");
        const [itemSnap, stateSnap] = await Promise.all([getDoc(itemRef), getDoc(stateRef)]);

        const hasItem = itemSnap.exists();
        const st = stateSnap.exists() ? stateSnap.data() : {};

        setPlaidConn({
          hasItem,
          status: st.connectionStatus || (hasItem ? "ok" : "none"),
          errorCode: st.errorCode || null,
          updatedAt: st.updatedAt || null,
        });
      } catch (e) {
        console.error("Plaid state load failed:", e);
      }
    };
    run();
  }, []);

  // Subscribe to users/{uid}/plaid/accounts/byId to know if any accounts are linked
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const unsub = onSnapshot(
      collection(db, "users", user.uid, "plaid", "accounts", "byId"),
      (snap) => setAccountsLinked(!snap.empty),
      (err) => {
        console.error("accounts subscription failed:", err);
        setAccountsLinked(false);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    // Read deep-link params from email buttons
    const tx = searchParams.get("tx");
    const filter = searchParams.get("filter");

        if (filter === "needs") {
      setFilterNeedsAttention(true);
      setFilterConfirmOnly(false);

      // Reset other filters so "needs action" is actually clean + obvious
      setSearchTerm("");
      setFilterCategory("");
      setFilterSubcategory("");
      setFilterAccount("");
      setFilterAmount("");
      setPage(1);
    }

    // ✅ Dashboard deep-link: Needs Confirmation
    if (filter === "confirm" || filter === "needsConfirmation" || filter === "confirmOnly") {
      setFilterConfirmOnly(true);
      setFilterNeedsAttention(false);

      // Reset other filters so "needs confirmation" is actually clean + obvious
      setSearchTerm("");
      setFilterCategory("");
      setFilterSubcategory("");
      setFilterAccount("");
      setFilterAmount("");
      setPage(1);
    }

    if (tx) {
      // trigger a one-time scroll once transactions load
      setScrollToTxId(tx);
    }

    // Clean the URL so future navigations aren’t affected
    const cleaned = new URLSearchParams(searchParams);
    cleaned.delete("tx");
    cleaned.delete("filter");
    setSearchParams(cleaned, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function loadBudgets() {
      const user = auth.currentUser;
      if (!user) return;

      const q = query(collection(db, "budgets"), where("userId", "==", user.uid));
      const snapshot = await getDocs(q);
      const fetched = snapshot.docs.map((doc) => doc.data());

      setBudgets(fetched);
    }
    loadBudgets();
  }, []);

  useEffect(() => {
    // Fetch even if budgets are empty (new user).
    // We can still show transactions and mark them “needs budget”.
    fetchTransactions();
    // budgets in deps so suggestions refresh when budgets arrive later
  }, [rules, budgets]);

  useEffect(() => {
    const idToScroll = scrollToTxId || globalScrollToTxId;

    if (idToScroll && transactions.length > 0) {
      const timeout = setTimeout(() => {
        const el = refsById.current[idToScroll];
        if (el) {
          // Compute a vertical target (account for your sticky header ~80–120px)
          const rect = el.getBoundingClientRect();
          const y = window.scrollY + rect.top - 120; // tweak offset as needed

          // Smooth vertical scroll to the row AND snap horizontal scroll to the left
          window.scrollTo({ top: Math.max(0, y), left: 0, behavior: "smooth" });

          // Belt & suspenders: after the smooth scroll starts, nudge X to 0 again
          setTimeout(() => {
            try {
              window.scrollTo({ left: 0, top: window.scrollY });
              document.documentElement.scrollLeft = 0;
              document.body.scrollLeft = 0;
            } catch {}
            globalScrollToTxId = null;
            setScrollToTxId(null);
          }, 450);
        }
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [scrollToTxId, transactions]);

  const handleCategorySave = async (id, category, subcategory) => {
    const txRaw = transactions.find((t) => t.id === id);

    // ✨ Always enrich the tx
    const tx = {
      ...txRaw,
      accountName: txRaw.accountName || txRaw.account_name || "",
      bankCategory: Array.isArray(txRaw.category) ? txRaw.category[0] : txRaw.bankCategory || "",
      transactionType: txRaw.transaction_type || "",
      mcc: txRaw.merchant_category_code || "",
    };

    setShowRuleModal({
      tx,
      initialCategory: category,
      initialSubcategory: subcategory,
    });
  };

  const [txToUpdateAfterModal, setTxToUpdateAfterModal] = useState(null);

  const handleShowMiniModal = (mode, defaultCat = "", txId = null) => {
    setMiniModalMode(mode);
    setDefaultMiniCategory(defaultCat);
    setTxToUpdateAfterModal(txId); // 🧠 Track which tx triggered the modal
    setShowMiniModal(true);
  };

  const handleMiniSave = async (newCategory, newSubcategory) => {
    setShowMiniModal(false);

    if (txToUpdateAfterModal) {
      await handleCategoryChange(txToUpdateAfterModal, newCategory, newSubcategory);
      setTxToUpdateAfterModal(null); // 🧹 clear after use
    }

    await fullyRefreshEverything();
  };

  const handleConfirmSuggestion = (tx) => setShowConfirmModal(tx);

  // ✅ Confirm selected match ONLY (no rule saved)
  const handleConfirmOnly = async (tx) => {
    try {
      if (!tx?.id) return;

      const draft = draftChanges[tx.id] || {};

      const txForConfirm = {
        ...tx,
        matchedCategory: draft.draftCategory ?? tx.matchedCategory,
        matchedSubcategory: draft.draftSubcategory ?? tx.matchedSubcategory,
      };

      const { category, subcategory } = resolveChosenPair(txForConfirm, budgets);

      if (!isActiveBudgetOption(category, subcategory, budgets)) {
        alert("Please select a valid category and subcategory before confirming.");
        return;
      }

      const ref = doc(db, "transactions", tx.id);
      await updateDoc(ref, {
        category,
        subcategory,
        categoryMatch: {
          type: "confirmed",
          category,
          subcategory,
          source: "user",
        },
        needsReview: false,
        status: "confirmed",
        type: "confirmed",
        confirmedAt: serverTimestamp(),
      });

      setTransactions((prev) =>
        prev.map((t) =>
          t.id === tx.id
            ? {
                ...t,
                matchedCategory: category,
                matchedSubcategory: subcategory,
                matchedSource: "user",
                category,
                subcategory,
                needsReview: false,
                status: "confirmed",
                reviewMessage: null,
              }
            : t
        )
      );

      // ✅ Clear any unsaved draft for this tx now that Firestore is confirmed
      setDraftChanges((prev) => {
        const next = { ...prev };
        delete next[tx.id];
        return next;
      });

    } catch (e) {
      console.error("Confirm-only failed:", e);
    }
  };

  const handleConfirmAndSaveRule = async (tx) => {
    const user = auth.currentUser;
    if (!user || !tx?.id) return;

    const { category, subcategory } = resolveChosenPair(tx, budgets);
    if (!isActiveBudgetOption(category, subcategory, budgets)) {
      alert("Please select a valid category and subcategory before saving a rule.");
      return;
    }

    // 🟢 Conflict check
    const conflicts = await checkForRuleConflict({
      vendor: tx.vendor,
      plaidCategory: Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || "",
    });

    if (conflicts.length > 0) {
      setShowConflictModal({
        existing: conflicts[0],
        newRule: {
          vendor: tx.vendor,
          plaidCategory: Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || "",
          category,
          subcategory,
        },
        onReplace: async () => {
          const conflictRef = doc(db, "rules", conflicts[0].id);
          await updateDoc(conflictRef, { category, subcategory });
          setShowConflictModal(null);
          await handleConfirmAndSaveRule(tx);
        },
        onAddMore: () => {
          setShowRuleModal({
            tx,
            initialCategory: category,
            initialSubcategory: subcategory,
          });
          setShowConflictModal(null);
        },
        onCancel: () => setShowConflictModal(null),
      });
      return;
    }

    // ✅ confirm tx
    const ref = doc(db, "transactions", tx.id);
    await updateDoc(ref, {
      category,
      subcategory,
      categoryMatch: {
        type: "confirmed",
        category,
        subcategory,
        source: "user",
      },
      needsReview: false,
      status: "confirmed",
      type: "rule",
      confirmedAt: serverTimestamp(),
    });

    // ✅ save rule
    await saveUserRule({
      userId: user.uid,
      vendor: (tx.vendor || "").toLowerCase(),
      vendorMatchType: "contains",
      plaidCategory: (
        Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || ""
      ).toLowerCase(),
      category,
      subcategory,
    });

    // ✅ retro update
    const qBase = [
      where("userId", "==", user.uid),
      where("vendor", "==", (tx.vendor || "").toLowerCase()),
      where(
        "plaidCategory",
        "==",
        (Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || "").toLowerCase()
      ),
    ];

    const retroQ = tx.amount
      ? query(collection(db, "transactions"), ...qBase, where("amount", "==", Number(tx.amount)))
      : query(collection(db, "transactions"), ...qBase);

    const snapshot = await getDocs(retroQ);

    if (!snapshot.empty) {
      setShowUpdateModal({
        count: snapshot.size,
        onUpdate: async () => {
          const batch = writeBatch(db);
          snapshot.docs.forEach((docSnap) => {
            const r = doc(db, "transactions", docSnap.id);
            batch.update(r, {
              categoryMatch: {
                type: "confirmed",
                category,
                subcategory,
                source: "rule",
              },
              category,
              subcategory,
              needsReview: false,
              status: "confirmed",
              type: "rule",
              confirmedAt: serverTimestamp(),
            });
          });

          await batch.commit();
          setShowUpdateModal(null);
        },
        onSkip: () => setShowUpdateModal(null),
        onCancel: () => setShowUpdateModal(null),
      });
    }

    setTransactions((prev) =>
      prev.map((t) =>
        t.id === tx.id
          ? {
              ...t,
              matchedCategory: category,
              matchedSubcategory: subcategory,
              matchedSource: "user",
              needsReview: false,
              reviewMessage: null,
            }
          : t
      )
    );

    setShowConfirmModal(null);
    setScrollToTxId(tx.id);
    await fullyRefreshEverything();
  };


  const handleConfirmWithoutRule = async (tx) => {
    const user = auth.currentUser;
    if (!user || !tx?.id) return;

    const { category, subcategory } = resolveChosenPair(tx, budgets);
    if (!isActiveBudgetOption(category, subcategory, budgets)) {
      alert("Please select a valid category and subcategory before confirming.");
      return;
    }

    const ref = doc(db, "transactions", tx.id);
    await updateDoc(ref, {
      category,
      subcategory,
      categoryMatch: {
        type: "confirmed",
        category,
        subcategory,
        source: "user",
      },
      needsReview: false,
      status: "confirmed",
      type: "confirmed",
      confirmedAt: serverTimestamp(),
    });

    // ✅ Pseudo rule upsert
    const q = query(
      collection(db, "pseudoRules"),
      where("userId", "==", user.uid),
      where("vendor", "==", tx.vendor || ""),
      where(
        "plaidCategory",
        "==",
        Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || ""
      )
    );

    const snapshot = await getDocs(q);
    let updated = false;

    snapshot.forEach((docSnap) => {
      const pr = docSnap.data();
      const amountDiff =
        Math.abs(Number(pr.amount) - Number(tx.amount)) / Math.abs(Number(pr.amount) || 1);

      if (amountDiff <= 0.2) {
        const prRef = doc(db, "pseudoRules", docSnap.id);
        updateDoc(prRef, {
          category,
          subcategory,
          updatedAt: new Date(),
        });
        updated = true;
      }
    });

    if (!updated) {
      await addDoc(collection(db, "pseudoRules"), {
        userId: user.uid,
        vendor: tx.vendor || "",
        amount: tx.amount,
        plaidCategory: Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || "",
        category,
        subcategory,
        createdAt: new Date(),
      });
    }

    setTransactions((prev) =>
      prev.map((t) =>
        t.id === tx.id
          ? {
              ...t,
              matchedCategory: category,
              matchedSubcategory: subcategory,
              matchedSource: "user",
              needsReview: false,
              reviewMessage: null,
            }
          : t
      )
    );

    setShowConfirmModal(null);
  };

  const handleSaveRule = async (draft) => {
    const user = auth.currentUser;
    const userId = user?.uid;

    const tx = showRuleModal?.tx;
    if (!userId || !tx) return;

    const {
      vendor,
      category,
      subcategory,
      amount,
      plaidCategory,
      amountMatchType,
      amountMatchValue,
      vendorMatchType,
      date,
      dateMatchType,
      dateMatchValue
    } = draft || {};

    // Hard-stop: allow opening Create Rule even if blank, but block saving until valid
    if (!isActiveBudgetOption(category, subcategory, budgets)) {
      alert("Pick a valid category and subcategory before saving the rule.");
      return;
    }

    // ✅ Use the edited vendor from the modal (fallback to tx.vendor)
    const finalVendor = String(vendor ?? tx.vendor ?? "").trim();

    // ✅ Use plaidCategory from modal if present, otherwise fallback to tx
    const finalPlaidCategory = String(
      plaidCategory ??
        (Array.isArray(tx.category) ? tx.category[0] : (tx.bankCategory || ""))
    );

    // ✅ Conflict check should use the edited vendor too
    const conflicts = await checkForRuleConflict({
      vendor: finalVendor,
      plaidCategory: finalPlaidCategory,
      amount
    });

    if (conflicts.length > 0) {
      setShowConflictModal({
        existing: conflicts[0],
        newRule: {
          vendor: finalVendor,
          plaidCategory: finalPlaidCategory,
          amount,
          category,
          subcategory
        },
        onReplace: async () => {
          const conflictRef = doc(db, "rules", conflicts[0].id);
          await updateDoc(conflictRef, { category, subcategory });
          setShowConflictModal(null);
        },
        onAddMore: () => {
          setShowRuleModal({ tx, initialCategory: category, initialSubcategory: subcategory });
          setShowConflictModal(null);
        },
        onCancel: () => setShowConflictModal(null)
      });
      return;
    }

    // Update the transaction (category/subcategory only; vendor stays as-is on the tx doc)
    const ref = doc(db, "transactions", tx.id);
    await updateDoc(ref, {
      category,
      subcategory,
      categoryMatch: {
        type: "confirmed",
        category,
        subcategory,
        source: "user",
      },
      needsReview: false,
      // ✅ top-level for reporting
      status: "confirmed",
      type: "rule",
      confirmedAt: serverTimestamp(),
    });

    // ✅ Save rule using edited vendor
    await saveUserRule({
      userId,
      vendor: finalVendor,
      category,
      subcategory,
      amount,
      plaidCategory: finalPlaidCategory,
      amountMatchType,
      amountMatchValue,
      vendorMatchType,
      date,
      dateMatchType,
      dateMatchValue
    });

    // ✅ After saving the rule, look for past matches
    const qBase = [
      where("userId", "==", user.uid),
      where("vendor", "==", finalVendor.toLowerCase()),
      where("plaidCategory", "==", finalPlaidCategory.toLowerCase())
    ];

    const retroQ = tx.amount
      ? query(collection(db, "transactions"), ...qBase, where("amount", "==", Number(tx.amount)))
      : query(collection(db, "transactions"), ...qBase);

    const snapshot = await getDocs(retroQ);

    const unconfirmedMatches = snapshot.docs.filter(docSnap => {
      const d = docSnap.data();
      return !d.categoryMatch; // Only apply to unconfirmed transactions
    });

    if (unconfirmedMatches.length > 0) {
      setShowUpdateModal({
        count: unconfirmedMatches.length,
        onUpdate: async () => {
          const batch = writeBatch(db);
          unconfirmedMatches.forEach(docSnap => {
            const ref = doc(db, "transactions", docSnap.id);
            batch.update(ref, {
              categoryMatch: {
                type: "confirmed",
                category,
                subcategory,
                source: "rule",
              },
              category,
              subcategory,
              needsReview: false,
              // ✅ top-level for reporting
              status: "confirmed",
              type: "rule",
              confirmedAt: serverTimestamp(),
            });
          });

          await batch.commit();
          setShowUpdateModal(null);
        },
        onSkip: () => setShowUpdateModal(null),
        onCancel: () => setShowUpdateModal(null),
      });
    }

    setTransactions(transactions.map((t) => t.id === tx.id ? {
      ...t,
      matchedCategory: category,
      matchedSubcategory: subcategory,
      matchedSource: "user",
      needsReview: false,
      reviewMessage: null,
    } : t));

    setShowRuleModal(null);
    globalScrollToTxId = tx.id;
    await fullyRefreshEverything();
  };
  
  const matchesFilters = (tx) => {
    const matchesSearch = !searchTerm || tx.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) || tx.memo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || tx.matchedCategory === filterCategory;
    const matchesSubcategory = !filterSubcategory || tx.matchedSubcategory === filterSubcategory;
    const matchesAccount = !filterAccount || tx.accountId?.toLowerCase().includes(filterAccount.toLowerCase());

    let matchesAmount = true;
    if (filterAmount) {
      const val = parseFloat(filterAmount);
      if (filterAmount.startsWith("-")) {
        matchesAmount = parseFloat(tx.amount) === val;
      } else {
        matchesAmount = Math.abs(parseFloat(tx.amount)) === val;
      }
    }

    const txMs = txDateToMillis(tx);
    const matchesDate =
      (!filterDateRange.start || (txMs && txMs >= Date.parse(filterDateRange.start + 'T00:00:00Z'))) &&
      (!filterDateRange.end   || (txMs && txMs <= Date.parse(filterDateRange.end   + 'T23:59:59Z')));

    const matchesType = !filterType ||
      (filterType === "income" && tx.amount > 0) ||
      (filterType === "expense" && tx.amount < 0);

    const matchesNeedsAttention = !filterNeedsAttention || (tx.needsReview === true || tx.matchedSource === "suggested");
    const isMatched = tx.matchedSource === "user" || tx.matchedSource === "rule" || tx.matchedSource === "wizard";
    const isSuggested = 
      !isMatched &&
      (
        tx.matchedSource === "suggested" || 
        tx.matchedSource === "pseudoRule" || 
        (!!tx.suggestedCategory && !tx.matchedCategory)
      );

    const matchesConfirmOnly = !filterConfirmOnly || isSuggested;

    const matchesStatus =
      filterStatus === "all" ? (isMatched || isSuggested)
      : filterStatus === "matched" ? isMatched
      : filterStatus === "suggested" ? isSuggested
      : true;

    return matchesSearch && matchesCategory && matchesSubcategory && matchesAccount &&
       matchesDate && matchesAmount && matchesType &&
       matchesConfirmOnly && matchesNeedsAttention && matchesStatus;
  };
  
  const handleCategoryChange = async (id, category, subcategory) => {
    const user = auth.currentUser;
    if (!user) return;

    const ref = doc(db, "transactions", id);
    await updateDoc(ref, {
      categoryMatch: {
        type: "manual",
        category,
        subcategory,
        source: "user",
      },
      category,
      subcategory,
      needsReview: false,
      // ✅ top-level for reporting
      status: "confirmed",
      type: "manual",
      confirmedAt: serverTimestamp(),
    });

    // ✅ NEW: Create a pseudo rule when the user manually changes a category
    const tx = transactions.find((t) => t.id === id);
    if (tx) {
      // ✅ Try to find an existing pseudo rule
      const q = query(
        collection(db, "pseudoRules"),
        where("userId", "==", user.uid),
        where("vendor", "==", tx.vendor || ""),
        where("plaidCategory", "==", Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || "")
      );

      const snapshot = await getDocs(q);

      let updated = false;

      snapshot.forEach(docSnap => {
        const pr = docSnap.data();
        const amountDiff = Math.abs(Number(pr.amount) - Number(tx.amount)) / Math.abs(Number(pr.amount) || 1);
        if (amountDiff <= 0.2) {
          // ✅ Found an existing pseudo rule → update it
          const ref = doc(db, "pseudoRules", docSnap.id);
          updateDoc(ref, {
            category,
            subcategory,
            updatedAt: new Date()
          });
          updated = true;
        }
      });

      if (!updated) {
        // ✅ No close match → create new pseudo rule
        await addDoc(collection(db, "pseudoRules"), {
          userId: user.uid,
          vendor: tx.vendor || "",
          amount: tx.amount,
          plaidCategory: Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || "",
          category,
          subcategory,
          createdAt: new Date(),
        });
      }
    }

    setTransactions((prev) =>
      prev.map((tx) =>
        tx.id === id
          ? {
              ...tx,
              matchedCategory: category,
              matchedSubcategory: subcategory,
              matchedSource: "user",
              needsReview: false,
              reviewMessage: null,
            }
          : tx
      )
    );
  };

    const bulkSubcategoryOptions =
    (budgets.find((b) => toLabel(b.category) === toLabel(bulkCategory))?.subcategories || [])
      .map((s) => toLabel(typeof s === "string" ? s : (s?.name ?? s)));

  const handleBulkReassign = async () => {
    if (isMobile) return; // desktop-only
    if (selectedTxIds.size === 0) return;

    if (!bulkCategory || !bulkSubcategory) {
      alert("Please choose a category and subcategory first.");
      return;
    }

    const ids = Array.from(selectedTxIds);

    try {
      // Firestore batches cap at 500 ops; keep buffer
      const CHUNK = 450;

      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK);
        const batch = writeBatch(db);

        chunk.forEach((id) => {
          const ref = doc(db, "transactions", id);
          batch.update(ref, {
            categoryMatch: {
              type: "manual",
              category: bulkCategory,
              subcategory: bulkSubcategory,
              source: "user",
            },
            category: bulkCategory,
            subcategory: bulkSubcategory,
            matchedCategory: bulkCategory,
            matchedSubcategory: bulkSubcategory,
            matchedSource: "user",
            needsReview: false,
            status: "confirmed",
            type: "manual",
            confirmedAt: serverTimestamp(),
          });
        });

        await batch.commit();
      }

      // Update UI locally
      setTransactions((prev) =>
        prev.map((t) =>
          selectedTxIds.has(t.id)
            ? {
                ...t,
                category: bulkCategory,
                subcategory: bulkSubcategory,
                matchedCategory: bulkCategory,
                matchedSubcategory: bulkSubcategory,
                matchedSource: "user",
                needsReview: false,
                status: "confirmed",
                type: "manual",
              }
            : t
        )
      );

      clearBulkSelection();
    } catch (e) {
      console.error("Bulk reassign failed:", e);
      alert("Bulk reassign failed. Check console for details.");
    }
  };

const handleBulkDelete = async () => {
  console.log("[BulkDelete] clicked", {
    isMobile,
    selectedCount: selectedTxIds?.size,
  });
  if (isMobile) return; // desktop-only
  if (selectedTxIds.size === 0) return;

  const ok = window.confirm(
    `Delete ${selectedTxIds.size} selected transaction(s)? This cannot be undone.`
  );
  if (!ok) return;

  try {
    const selected = transactions.filter((t) => selectedTxIds.has(t.id));

    // Split Plaid vs manual
    const plaidIds = selected.map(getPlaidTxIdFromTx).filter(Boolean);
    const manualRootIds = selected.filter((t) => !getPlaidTxIdFromTx(t)).map((t) => t.id);

    // Plaid-backed: write tombstones + delete root docs server-side
    if (plaidIds.length) {
      await authedPost("/deleteTransactionsBulk", {
        plaid_tx_ids: plaidIds,
        source: "transactions_page_bulk",
      });
    }

    // Manual/non-Plaid: delete root docs client-side
    if (manualRootIds.length) {
      const CHUNK = 450;
      for (let i = 0; i < manualRootIds.length; i += CHUNK) {
        const chunk = manualRootIds.slice(i, i + CHUNK);
        const batch = writeBatch(db);
        chunk.forEach((id) => batch.delete(doc(db, "transactions", id)));
        await batch.commit();
      }
    }

    // Update UI
    setTransactions((prev) => prev.filter((t) => !selectedTxIds.has(t.id)));
    clearBulkSelection();
  } catch (e) {
    console.error("Bulk delete failed:", e);
    alert(e?.message || "Bulk delete failed. Check console for details.");
  }
};

const filteredTransactions = transactions.filter(matchesFilters);
const totalTransactions = filteredTransactions.length;
const indexOfLast = page * transactionsPerPage;
const indexOfFirst = indexOfLast - transactionsPerPage;

const paginatedTransactions = filteredTransactions.slice(indexOfFirst, indexOfLast);

const styleSheet = document.styleSheets[0];
const keyframes =
`@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}`;

if (styleSheet && styleSheet.insertRule) {
  try {
    styleSheet.insertRule(keyframes, styleSheet.cssRules.length);
  } catch (err) {
    // prevent duplicate keyframe insertion
  }
}

 // 👀 Decide which prompts to show
 // Show the “connect” CTA only when there are truly NO linked accounts.
 const showConnectCTA  = accountsLinked === false;
 const showBrokenAlert =
   plaidConn.errorCode === "ITEM_LOGIN_REQUIRED" || plaidConn.status === "error";

return (
  <>
  {(isRefreshing || !hasLoadedOnce) && (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(255, 255, 255, 0.7)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      backdropFilter: "blur(3px)",
      pointerEvents: "all"
    }}>
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "1rem",
        backgroundColor: "#fff",
        padding: "1.5rem 2rem",
        borderRadius: "8px",
        boxShadow: "0 4px 10px rgba(0,0,0,0.1)"
      }}>
        <div className="trelli-spinner" />
        <div style={{
          fontSize: "1.2rem",
          fontWeight: "bold",
          color: "#00B074"
        }}>
          {isRefreshing ? "Refreshing..." : "Loading transactions..."}
        </div>
      </div>
    </div>
  )}
  <div
    style={{
      filter: isRefreshing ? "blur(2px)" : "none",
      transition: "filter 0.3s ease-in-out"
    }}
  >
    <div
      style={{
        color: "#111",
        backgroundColor: "#fff",
        padding: isMobile ? "8px 8px 12px" : "1rem",
        width: "100%",
        display: "flex",
        flexDirection: "column"
      }}
    >
      {showConnectCTA && (
        <div style={{
          border: "1px solid #d3e6dd",
          background: "#f6fffa",
          color: "#0f5132",
          padding: "16px",
          borderRadius: "8px",
          marginBottom: "16px"
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>🔗 Connect your accounts</div>
          <div style={{ marginBottom: 12 }}>
            Link your bank and credit card accounts so Trelli can pull transactions automatically.
          </div>
          <button
            onClick={() => {
              try { localStorage.setItem("trelli_settings_tab", "plaid"); } catch {}
              navigate("/settings?tab=plaid");
            }}
            style={{
              height: 40,
              padding: "0 14px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              whiteSpace: "nowrap",
              fontWeight: 600,
              borderRadius: 12,
              border: "none",
              cursor: "pointer",
              background: TRELLI_GREEN,
              color: "#fff",
            }}
          >
            Go to Settings to Link Accounts
          </button>
        </div>
      )}

      {showBrokenAlert && (
        <div style={{
          border: "1px solid #ffe69c",
          background: "#fff8e1",
          color: "#664d03",
          padding: "16px",
          borderRadius: "8px",
          marginBottom: "16px"
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠️ Bank connection needs attention</div>
          <div style={{ marginBottom: 12 }}>
            We’re not receiving new transactions because your bank needs you to re-authenticate.
          </div>
          <button
            onClick={() => {
              try { localStorage.setItem("trelli_settings_tab", "plaid"); } catch {}
              window.dispatchEvent(new CustomEvent("trelli:navigate", { detail: "settings" }));
              navigate("/settings?tab=plaid");
            }}
            style={toolbarBtnStyle}
          >
            Fix connection
          </button>
        </div>
      )}
      
      <h2>Transactions</h2>

      {!isMobile && (
        <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
          <button
            onClick={() => setShowAddModal(true)}
            style={{ ...toolbarBtnStyle, maxWidth: "200px" }}
            title="Add a manual transaction"
          >
            Add Transaction
          </button>

          <button
            onClick={() => setShowUpload(!showUpload)}
            style={{ ...toolbarBtnStyle, maxWidth: "200px" }}
          >
            {showUpload ? "Close Upload" : "Upload CSV"}
          </button>

          <DeleteMockTransactionsButton
            label="Delete All Transactions"
            style={{ ...toolbarBtnStyle, background: "#d9534f", maxWidth: "200px" }}
          />
        </div>
      )}

      {isMobile && (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 20,
            background: "#fff",
            borderBottom: "1px solid #eee",
            padding: "6px 8px",
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}
        >
          {/* "+" replaces the desktop "Add Transaction" button */}
          <button
            aria-label="Add transaction"
            onClick={() => setShowAddModal(true)}
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              border: "none",
              fontSize: "22px",
              lineHeight: 1,
              fontWeight: 700,
              background: TRELLI_GREEN,
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            title="Add transaction"
          >
            +
          </button>

          {/* Search next to + */}
          <input
            type="text"
            placeholder="Search vendor or memo"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              height: 36,
              borderRadius: 10,
              border: "1px solid #ccc",
              padding: "0 10px"
            }}
          />

          {/* Compact Filter button to open the sheet/drawer */}
          <button
            onClick={() => setShowMobileFilter(true)}
            style={{
              position: "relative",
              height: 36,
              padding: "0 12px",
              borderRadius: 10,
              border: "1px solid #ccc",
              background: "#f8f8f8",
              color: "#111",
              fontWeight: 600
            }}
            title="Filter"
          >
            Filter
            {activeFilterCount > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  minWidth: 18,
                  height: 18,
                  padding: "0 5px",
                  borderRadius: 999,
                  background: TRELLI_YELLOW,
                  color: "#111",
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: "18px",
                  textAlign: "center",
                  boxShadow: "0 0 0 1px #fff"
                }}
              >
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      )}

      {showUpload && <UploadTransactions />}
      
      {!isMobile && (
        <div style={{
          position: "sticky",
          paddingTop: "1rem",
          top: 0,
          zIndex: 10,
          background: "#fff",
          borderBottom: "1px solid #ccc",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
        }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }}>
          <input type="text" placeholder="Search vendor or memo" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          <select value={filterCategory} onChange={(e) => {
            setFilterCategory(e.target.value);
            setFilterSubcategory("");
          }}>
            <option value="">All Categories</option>
            {budgets.map((b, i) => {
              const label = toLabel(b?.category ?? b);
              return <option key={i} value={label}>{label}</option>;
            })}
          </select>
          <select value={filterSubcategory} onChange={(e) => setFilterSubcategory(e.target.value)}>
            <option value="">All Subcategories</option>
            {(budgets.find((b) => toLabel(b.category) === filterCategory)?.subcategories || []).map((s, i) => {
              const label = toLabel(typeof s === "string" ? s : (s?.name ?? s));
              return <option key={i} value={label}>{label}</option>;
            })}
          </select>

          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">All Transactions</option>
            <option value="income">Income only</option>
            <option value="expense">Expense only</option>
          </select>
          <select value={filterAccount} onChange={(e) => setFilterAccount(e.target.value)}>
            <option value="">All Accounts</option>
            {uniqueAccounts.map((name, i) => (
              <option key={i} value={name}>{name}</option>
            ))}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">Matched & Suggested</option>
            <option value="matched">Matched Only</option>
            <option value="suggested">Suggested Only</option>
          </select>

          {/* Second line: Date range + Amount */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              flexBasis: "100%",          // ⬅️ forces this block onto a new line
              marginTop: "0.25rem"
            }}
          >

            <label style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
              <span style={{ opacity: 0.75 }}>From</span>
              <input
                type="date"
                aria-label="Start date"
                title="Start date (From)"
                value={filterDateRange.start}
                onChange={(e) =>
                  setFilterDateRange({ ...filterDateRange, start: e.target.value })
                }
              />
            </label>

            <span>–</span>

            <label style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
              <span style={{ opacity: 0.75 }}>To</span>
              <input
                type="date"
                aria-label="End date"
                title="End date (To)"
                value={filterDateRange.end}
                onChange={(e) =>
                  setFilterDateRange({ ...filterDateRange, end: e.target.value })
                }
              />
            </label>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginLeft: "1rem"
              }}
              title="Filter by exact amount (use negative for expenses)"
            >
              <input
                type="number"
                placeholder="Exact amount (e.g., -42.50)"
                value={filterAmount}
                onChange={(e) => setFilterAmount(e.target.value)}
                style={{ width: 180 }}
              />
            </label>
            {/* ✅ Custom Toggle Buttons */}
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span>Needs Confirmation</span>
              <div
                onClick={() => setFilterConfirmOnly(!filterConfirmOnly)}
                style={{
                  width: "42px",
                  height: "22px",
                  borderRadius: "20px",
                  backgroundColor: filterConfirmOnly ? "#00B074" : "#ccc",
                  position: "relative",
                  cursor: "pointer",
                  transition: "background-color 0.3s"
                }}
              >
                <div
                  style={{
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    position: "absolute",
                    top: "2px",
                    left: filterConfirmOnly ? "22px" : "2px",
                    transition: "left 0.3s"
                  }}
                ></div>
              </div>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span>Needs Attention</span>
              <div
                onClick={() => setFilterNeedsAttention(!filterNeedsAttention)}
                style={{
                  width: "42px",
                  height: "22px",
                  borderRadius: "20px",
                  backgroundColor: filterNeedsAttention ? "#00B074" : "#ccc",
                  position: "relative",
                  cursor: "pointer",
                  transition: "background-color 0.3s"
                }}
              >
                <div
                  style={{
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    position: "absolute",
                    top: "2px",
                    left: filterNeedsAttention ? "22px" : "2px",
                    transition: "left 0.3s"
                  }}
                ></div>
              </div>
            </label>
          </div>
        </div>
        </div>
      )}

      {/* Bulk actions (desktop-only) */}
      {!isMobile && selectedTxIds.size > 0 && (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 11,
            background: "#fff",
            border: "1px solid #d3e6dd",
            borderRadius: 12,
            padding: "10px 12px",
            marginBottom: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontWeight: 800 }}>
            {selectedTxIds.size} selected
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <select
              value={bulkCategory}
              onChange={(e) => {
                setBulkCategory(e.target.value);
                setBulkSubcategory("");
              }}
              style={{ height: 38, borderRadius: 10, border: "1px solid #ccc", padding: "0 10px" }}
            >
              <option value="">Category…</option>
              {budgets.map((b, i) => {
                const label = toLabel(b?.category ?? b);
                return (
                  <option key={i} value={label}>
                    {label}
                  </option>
                );
              })}
            </select>

            <select
              value={bulkSubcategory}
              onChange={(e) => setBulkSubcategory(e.target.value)}
              disabled={!bulkCategory}
              style={{ height: 38, borderRadius: 10, border: "1px solid #ccc", padding: "0 10px" }}
            >
              <option value="">Subcategory…</option>
              {bulkSubcategoryOptions.map((s, i) => (
                <option key={i} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <button
              onClick={handleBulkReassign}
              style={{ ...toolbarBtnStyle, height: 38 }}
              title="Apply category/subcategory to selected transactions"
            >
              Reassign selected
            </button>
          </div>

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={handleBulkDelete}
              style={{
                ...toolbarBtnStyle,
                height: 38,
                background: "#d9534f",
              }}
              title="Delete selected transactions"
            >
              Delete selected
            </button>

            <button
              onClick={clearBulkSelection}
              style={{ ...toolbarBtnStyle, height: 38, background: "#eee", color: "#222" }}
              title="Clear selection"
            >
              Clear
            </button>
          </div>
        </div>
      )}
          
        {/* HORIZONTAL SCROLL CONTAINER */}
        <div
          style={{
            overflowX: isMobile ? "hidden" : "auto",
            width: "100%",
            boxSizing: "border-box",
            paddingRight: isMobile ? "8px" : "16px"
          }}
        >
          <div style={{ minWidth: isMobile ? "auto" : "1204px" }}>
            {isMobile && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  gap: 8,
                  padding: "6px 8px",
                  background: "#f7f7f7",
                  borderBottom: "1px solid #e5e5e5"
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>Vendor</div>
                  <div style={{ fontSize: "0.8rem", color: "#666" }}>Category • Subcategory</div>
                </div>

                {/* Amount with Actions nested underneath */}
                <div style={{ width: 120, textAlign: "center" }}>
                  <div style={{ fontWeight: 700 }}>Amount</div>
                  <div style={{ fontSize: "0.8rem", color: "#666" }}>Actions</div>
                </div>
              </div>
            )}

            {/* HEADER ROW */}
            {!isMobile && (() => {
              const visibleIds = paginatedTransactions.map((t) => t.id);
              const selectedOnPage = visibleIds.filter((id) => selectedTxIds.has(id)).length;
              const allOnPage = visibleIds.length > 0 && selectedOnPage === visibleIds.length;
              const noneOnPage = selectedOnPage === 0;
              const indeterminate = !noneOnPage && !allOnPage;

              // keep master checkbox indeterminate in sync
              if (masterCheckboxRef.current) {
                masterCheckboxRef.current.indeterminate = indeterminate;
              }

              return (
                <div
                  style={{
                    display: "flex",
                    fontWeight: "bold",
                    background: "#f0f0f0",
                    borderBottom: "1px solid #ccc",
                    color: "#222",
                    top: 0,
                    zIndex: 1,
                    padding: "0.75rem 0.5rem",
                    fontSize: "0.95rem",
                  }}
                >
                  {/* Master checkbox cell */}
                  <div
                    style={{
                      flex: `0 0 ${headerWidths[0]}`,
                      maxWidth: headerWidths[0],
                      padding: "0 4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <input
                      ref={masterCheckboxRef}
                      type="checkbox"
                      checked={allOnPage}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const ids = paginatedTransactions.map((t) => t.id);

                        setSelectedTxIds((prev) => {
                          const next = new Set(prev);
                          if (checked) ids.forEach((id) => next.add(id));
                          else ids.forEach((id) => next.delete(id));
                          return next;
                        });
                      }}
                      aria-label="Select all transactions on this page"
                      title="Select all on this page"
                    />
                  </div>

                  {/* Rest of headers */}
                  {headerLabels.slice(1).map((label, i) => (
                    <div
                      key={`${label}-${i}`}
                      style={{
                        flex: `0 0 ${headerWidths[i + 1]}`,
                        maxWidth: headerWidths[i + 1],
                        padding: "0 4px",
                        textAlign:
                          label === "Vendor" || label === "Memo" ? "left" : "center",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {label}
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* ROWS */}
            {paginatedTransactions.map((tx, idx) => {
              const matchedActive = isActiveBudgetOption(tx.matchedCategory, tx.matchedSubcategory, budgets);
              const suggestedActive = isActiveBudgetOption(tx.suggestedCategory, tx.suggestedSubcategory, budgets);

              const uiCategory =
                draftChanges[tx.id]?.draftCategory ?? tx.matchedCategory ?? tx.suggestedCategory ?? "";
              const uiSubcategory =
                draftChanges[tx.id]?.draftSubcategory ?? tx.matchedSubcategory ?? tx.suggestedSubcategory ?? "";

              const uiActive = isActiveBudgetOption(uiCategory, uiSubcategory, budgets);
              const eligibleConfirmSource =
                (!tx.matchedSource || tx.matchedSource === "suggested" || tx.matchedSource === "pseudoRule");

              const baseCategory = toLabel(tx.matchedCategory ?? tx.suggestedCategory ?? "").trim();
              const baseSubcategory = toLabel(tx.matchedSubcategory ?? tx.suggestedSubcategory ?? "").trim();

              const draftCatRaw = draftChanges[tx.id]?.draftCategory;
              const draftSubRaw = draftChanges[tx.id]?.draftSubcategory;
              const hasDraft = draftCatRaw != null || draftSubRaw != null;

              const hasUnsavedDraft =
                hasDraft &&
                (toLabel(uiCategory).trim() !== baseCategory || toLabel(uiSubcategory).trim() !== baseSubcategory);

              // ✅ Incomplete means the CURRENT UI selection is incomplete (includes drafts)
              const categoryBlank = !toLabel(uiCategory).trim();
              const subcategoryBlank = !toLabel(uiSubcategory).trim();

              // Covers legacy corruption even if it sneaks through somewhere
              const hasCorruptSub =
                isPlaceholderSubcategory(uiSubcategory) ||
                isPlaceholderSubcategory(tx.categoryMatch?.subcategory) ||
                isPlaceholderSubcategory(tx.suggestedSubcategory) ||
                isPlaceholderSubcategory(tx.matchedSubcategory);

              // ✅ “Assigned but not in budget” should also respect what’s currently selected in the UI
              const assignedButNotInBudget =
                (toLabel(uiCategory).trim() && toLabel(uiSubcategory).trim() && !uiActive);

              let status = "🟢";
              let reason = "";

              if (categoryBlank || subcategoryBlank || hasCorruptSub) {
                status = "🔴";
                reason = "Select a valid category and subcategory.";
              } else if (assignedButNotInBudget) {
                status = "🔴";
                reason = "Assigned category or subcategory does not exist in your budget.";
              } else if (tx.matchedSource === "user" || tx.matchedSource === "wizard" || tx.matchedSource === "rule") {
                status = "🟢";
                reason = "Confirmed.";
              } else if (
                ((!tx.matchedSource || tx.matchedSource === "suggested" || tx.matchedSource === "pseudoRule") &&
                  suggestedActive &&
                  !matchedActive)
              ) {
                status = "🟡";
                reason = "Suggested match. Please confirm.";
              } else {
                status = "🟢";
                reason = "Confirmed.";
              }

              return (
                <div key={tx.id} ref={(el) => (refsById.current[tx.id] = el)}>
                  {/* Desktop-only checkbox + row */}
                  {!isMobile ? (
                    <div style={{ display: "flex", alignItems: "stretch" }}>
                      <div
                        style={{
                          width: 44,
                          minWidth: 44,
                          maxWidth: 44,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderBottom: "1px solid #eee",
                          background:
                            status === "🔴" ? "#ffe5e5"
                            : status === "🟡" ? "#fffdea"
                            : idx % 2 === 0 ? "#fafafa" : "#fff",
                          borderLeft:
                            status === "🔴" ? "4px solid red"
                            : status === "🟡" ? "4px solid #F4C542"
                            : "4px solid transparent",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected(tx.id)}
                          onChange={() => toggleOneSelected(tx.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select transaction ${tx.vendor || ""}`}
                          title="Select"
                        />
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <TransactionRowStyled
                          isMobile={isMobile}
                          accountNameById={accountNameById}
                          onConfirmAndCreateRule={() => handleConfirmSuggestion(tx)}
                          onOpenConfirmAndRule={() => setShowConfirmModal(tx)}
                          onOpenRuleEditor={() => {
                            const initialCategory = tx.matchedSource === "suggested" ? tx.suggestedCategory : tx.matchedCategory;
                            const initialSubcategory = tx.matchedSource === "suggested" ? tx.suggestedSubcategory : tx.matchedSubcategory;

                            const txRaw = tx;
                            const enriched = {
                              ...txRaw,
                              accountName: txRaw.accountName || txRaw.account_name || "",
                              bankCategory: Array.isArray(txRaw.category) ? txRaw.category[0] : txRaw.bankCategory || "",
                              transactionType: txRaw.transaction_type || "",
                              mcc: txRaw.merchant_category_code || "",
                            };

                            setShowRuleModal({ tx: enriched, initialCategory, initialSubcategory });
                          }}
                          onOpenReassign={() => setShowReassignModal(tx)}
                          columnStyles={columnStyles}
                          singleAmount={true}
                          compactSelects={true}
                          tx={{
                            id: tx.id,
                            rowGap: "0",
                            vendor: tx.vendor,
                            accountId: tx.accountId || tx.account_id || "",
                            accountName: tx.accountName || tx.account_name || "",
                            bankCategory: Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || "",
                            income: tx.amount > 0 ? tx.amount : null,
                            expense: tx.amount < 0 ? tx.amount : null,
                            matchedSource: tx.matchedSource || "",
                            memo: tx.memo,
                            date: formatTxDate(tx),
                            category:
                              (draftChanges[tx.id]?.draftCategory ?? tx.matchedCategory ?? tx.suggestedCategory ?? "None"),
                            subcategory:
                              (draftChanges[tx.id]?.draftSubcategory ?? tx.matchedSubcategory ?? tx.suggestedSubcategory ?? "None"),
                            pendingDraft:
                              Boolean(draftChanges[tx.id]?.draftCategory || draftChanges[tx.id]?.draftSubcategory),
                            originalCategory:
                              (tx.matchedCategory ?? tx.suggestedCategory ?? "None"),
                            originalSubcategory:
                              (tx.matchedSubcategory ?? tx.suggestedSubcategory ?? "None"),
                            transactionType: tx.transaction_type || "",
                            mcc: tx.merchant_category_code || "",
                            status,
                            showConfirm: eligibleConfirmSource && uiActive,
                            reason: reason,
                            hasUnsavedDraft: hasUnsavedDraft,
                          }}
                          idx={idx}
                          onMemoSave={handleMemoChange}
                          onConfirm={() => handleConfirmOnly(tx)}
                          budgets={budgets}
                          onShowMiniModal={handleShowMiniModal}
                          onCategoryChange={(id, value, type) => {
                            if (type === "category") {
                              handleDraftCategoryChange(id, value);
                            } else {
                              handleDraftSubcategoryChange(id, value);
                            }
                          }}
                          onCancelDraft={(id) => handleCancelDraftChange(id)}
                          onEdit={() =>
                            handleCategorySave(
                              tx.id,
                              tx.matchedSource === "suggested"
                                ? tx.suggestedCategory
                                : tx.matchedCategory,
                              tx.matchedSource === "suggested"
                                ? tx.suggestedSubcategory
                                : tx.matchedSubcategory
                            )
                          }
                          onDelete={() => setShowDeleteModal(tx)}
                        />
                      </div>
                    </div>
                  ) : (
                    <TransactionRowStyled
                      isMobile={isMobile}
                      accountNameById={accountNameById}
                      onConfirmAndCreateRule={() => handleConfirmSuggestion(tx)}
                      onOpenConfirmAndRule={() => setShowConfirmModal(tx)}
                      onOpenRuleEditor={() => {
                        const initialCategory =
                          tx.matchedSource === "suggested" ? tx.suggestedCategory : tx.matchedCategory;

                        const initialSubcategory =
                          tx.matchedSource === "suggested" ? tx.suggestedSubcategory : tx.matchedSubcategory;

                        const txRaw = tx;
                        const enriched = {
                          ...txRaw,
                          accountName: txRaw.accountName || txRaw.account_name || "",
                          bankCategory: Array.isArray(txRaw.category) ? txRaw.category[0] : txRaw.bankCategory || "",
                          transactionType: txRaw.transaction_type || "",
                          mcc: txRaw.merchant_category_code || "",
                        };

                        setShowRuleModal({ tx: enriched, initialCategory, initialSubcategory });
                      }}
                      onOpenReassign={() => setShowReassignModal(tx)}
                      columnStyles={columnStyles}
                      singleAmount={true}
                      compactSelects={true}
                      tx={{
                        id: tx.id,
                        rowGap: "0",
                        vendor: tx.vendor,
                        accountId: tx.accountId || tx.account_id || "",
                        accountName: tx.accountName || tx.account_name || "",
                        bankCategory: Array.isArray(tx.category) ? tx.category[0] : tx.bankCategory || "",
                        income: tx.amount > 0 ? tx.amount : null,
                        expense: tx.amount < 0 ? tx.amount : null,
                        matchedSource: tx.matchedSource || "",
                        memo: tx.memo,
                        date: formatTxDate(tx),
                        category:
                          (draftChanges[tx.id]?.draftCategory ?? tx.matchedCategory ?? tx.suggestedCategory ?? "None"),
                        subcategory:
                          (draftChanges[tx.id]?.draftSubcategory ?? tx.matchedSubcategory ?? tx.suggestedSubcategory ?? "None"),
                        pendingDraft:
                          Boolean(draftChanges[tx.id]?.draftCategory || draftChanges[tx.id]?.draftSubcategory),
                        originalCategory:
                          (tx.matchedCategory ?? tx.suggestedCategory ?? "None"),
                        originalSubcategory:
                          (tx.matchedSubcategory ?? tx.suggestedSubcategory ?? "None"),
                        status,
                        showConfirm: eligibleConfirmSource && uiActive,
                        reason: reason,
                        hasUnsavedDraft: hasUnsavedDraft,
                      }}
                      idx={idx}
                      onMemoSave={handleMemoChange}
                      onConfirm={() => handleConfirmOnly(tx)}
                      budgets={budgets}
                      onShowMiniModal={handleShowMiniModal}
                      onCategoryChange={(id, value, type) => {
                        if (type === "category") handleDraftCategoryChange(id, value);
                        else handleDraftSubcategoryChange(id, value);
                      }}
                      onCancelDraft={(id) => handleCancelDraftChange(id)}
                      onEdit={() =>
                        handleCategorySave(
                          tx.id,
                          tx.matchedSource === "suggested"
                            ? tx.suggestedCategory
                            : tx.matchedCategory,
                          tx.matchedSource === "suggested"
                            ? tx.suggestedSubcategory
                            : tx.matchedSubcategory
                        )
                      }
                      onDelete={() => setShowDeleteModal(tx)}
                    />
                  )}
                </div>
              );
            })}
            {/* END ROWS */}

          </div>
          {/* END minWidth WRAPPER */}
        </div>
        {/* END SCROLL CONTAINER */}

      <div style={{ textAlign: "center", marginTop: "1rem" }}>
        <p>
          Showing {Math.min(indexOfFirst + 1, totalTransactions)}
          –{Math.min(indexOfLast, totalTransactions)} of {totalTransactions} transactions
        </p>
      </div>

      <div style={{ textAlign: "center", marginTop: "1rem" }}>
        <button
          onClick={() => setPage(prev => Math.max(prev - 1, 1))}
          disabled={page === 1}
          style={{ marginRight: "0.5rem" }}
        >
          Prev
        </button>

        {Array.from({ length: Math.ceil(totalTransactions / transactionsPerPage) }).map((_, i) => (
          <button
            key={i}
            onClick={() => setPage(i + 1)}
            style={{
              margin: "0 0.25rem",
              padding: "0.5rem 1rem",
              background: page === i + 1 ? "#00B074" : "#f0f0f0",
              border: "1px solid #ccc",
              cursor: "pointer"
            }}
          >
            {i + 1}
          </button>
        ))}

        <button
          onClick={() =>
            setPage(prev =>
              prev + 1 > Math.ceil(totalTransactions / transactionsPerPage) ? prev : prev + 1
            )
          }
          disabled={page === Math.ceil(totalTransactions / transactionsPerPage)}
          style={{ marginLeft: "0.5rem" }}
        >
          Next
        </button>
      </div>

      {showConfirmModal && (
        <RuleConfirmationModal
          tx={showConfirmModal}
          onConfirm={() => handleConfirmAndSaveRule(showConfirmModal)}
          onConfirmSingle={() => handleConfirmWithoutRule(showConfirmModal)}
          onEdit={() => {
            const txRaw = showConfirmModal;
            const tx = {
              ...txRaw,
              accountName: txRaw.accountName || txRaw.account_name || "",
              bankCategory: Array.isArray(txRaw.category) ? txRaw.category[0] : txRaw.bankCategory || "",
              transactionType: txRaw.transaction_type || "",
              mcc: txRaw.merchant_category_code || "",
            };
            setShowRuleModal({
              tx,
              initialCategory: txRaw.suggestedCategory,
              initialSubcategory: txRaw.suggestedSubcategory,
            });
            setShowConfirmModal(null);
          }}
          onCancel={() => setShowConfirmModal(null)}
        />
      )}

      {showMiniModal && (
        <MiniCategoryModal
          mode={miniModalMode}
          existingCategories={budgets.map((b) => b.category)}
          defaultCategory={defaultMiniCategory}
          onSave={(cat, subcat) => handleMiniSave(cat, subcat)}
          onClose={() => setShowMiniModal(false)}
        />
      )}

      {showRuleModal && (
        <RuleCreator
          tx={showRuleModal.tx}
          initialCategory={showRuleModal.initialCategory}
          initialSubcategory={showRuleModal.initialSubcategory}
          budgets={budgets}
          accountNameById={accountNameById}
          onClose={() => setShowRuleModal(null)}
          onSave={handleSaveRule}
        />
      )}

      {showConflictModal && (
        <RuleConflictModal
          existing={showConflictModal.existing}
          newRule={showConflictModal.newRule}
          onReplace={showConflictModal.onReplace}
          onAddMore={showConflictModal.onAddMore}
          onCancel={showConflictModal.onCancel}
        />
      )}

      {showUpdateModal && (
        <RuleUpdateModal
          count={showUpdateModal.count}
          onUpdate={showUpdateModal.onUpdate}
          onSkip={showUpdateModal.onSkip}
          onCancel={showUpdateModal.onCancel}
        />
      )}

      {showReassignModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", padding: "1rem", borderRadius: 8, width: "92%", maxWidth: 420, boxShadow: "0 10px 30px rgba(0,0,0,0.25)" }}>
            <h3 style={{ marginTop: 0, marginBottom: "0.5rem" }}>Reassign Transaction</h3>

            <div style={{ fontSize: "0.9rem", marginBottom: "0.75rem", color: "#333" }}>
              <div><strong>Vendor:</strong> {showReassignModal.vendor}</div>
              <div><strong>Date:</strong> {formatTxDate(showReassignModal)}</div>
              <div><strong>Amount:</strong> {Number(showReassignModal.amount).toLocaleString(undefined, { style: "currency", currency: "USD" })}</div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
              <label>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Category</div>
                <select
                  value={showReassignModal.matchedCategory || showReassignModal.suggestedCategory || ""}
                  onChange={(e) => setShowReassignModal(prev => ({ ...prev, matchedCategory: e.target.value, suggestedCategory: "" }))}
                  style={{ width: "100%", height: 38, borderRadius: 8, border: "1px solid #ccc", padding: "0 8px" }}
                >
                  <option value="">Select category</option>
                  {budgets.map((b, i) => {
                    const label = toLabel(b?.category ?? b);
                    return <option key={i} value={label}>{label}</option>;
                  })}
                </select>
              </label>

              <label>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Subcategory</div>
                <select
                  value={showReassignModal.matchedSubcategory || showReassignModal.suggestedSubcategory || ""}
                  onChange={(e) => setShowReassignModal(prev => ({ ...prev, matchedSubcategory: e.target.value, suggestedSubcategory: "" }))}
                  style={{ width: "100%", height: 38, borderRadius: 8, border: "1px solid #ccc", padding: "0 8px" }}
                >
                  <option value="">Select subcategory</option>
                  {(budgets.find((b) => toLabel(b.category) === toLabel(showReassignModal.matchedCategory || showReassignModal.suggestedCategory))?.subcategories || []).map((s, i) => {
                    const label = toLabel(typeof s === "string" ? s : (s?.name ?? s));
                    return <option key={i} value={label}>{label}</option>;
                  })}
                </select>
              </label>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
              <button
                onClick={() => setShowReassignModal(null)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 10,
                  border: "1px solid #ccc",
                  background: "#f0f0f0",
                  color: "#111",            // ← force black text
                  fontWeight: 600           // optional, matches other buttons’ weight
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const category =
                    showReassignModal?.matchedCategory || showReassignModal?.suggestedCategory;
                  const subcategory =
                    showReassignModal?.matchedSubcategory || showReassignModal?.suggestedSubcategory;

                  if (!showReassignModal?.id || !category || !subcategory) {
                    console.warn("[Reassign Save] missing fields", { category, subcategory, id: showReassignModal?.id });
                    return;
                  }

                  try {
                    const ref = doc(db, "transactions", showReassignModal.id);

                    await updateDoc(ref, {
                      categoryMatch: {
                        type: "manual",
                        category,
                        subcategory,
                        source: "user",
                      },
                      category,
                      subcategory,
                      matchedCategory: category,
                      matchedSubcategory: subcategory,
                      matchedSource: "user",
                      needsReview: false,
                      status: "confirmed",
                      type: "manual",
                      confirmedAt: serverTimestamp(),
                    });

                    setTransactions((prev) =>
                      prev.map((t) =>
                        t.id === showReassignModal.id
                          ? {
                              ...t,
                              category,
                              subcategory,
                              matchedCategory: category,
                              matchedSubcategory: subcategory,
                              matchedSource: "user",
                              needsReview: false,
                              status: "confirmed",
                              type: "manual",
                            }
                          : t
                      )
                    );

                    setShowReassignModal(null);
                  } catch (e) {
                    console.error("[Reassign Save] failed:", e);
                    alert(e?.message || "Reassign failed. Check console.");
                  }
                }}
                style={{ padding: "8px 12px", borderRadius: 10, border: "none", background: "#00B074", color: "#fff", fontWeight: 700 }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "#fff",
          border: "1px solid #ccc",
          padding: "2rem",
          zIndex: 9999,
          borderRadius: "8px",
          boxShadow: "0 0 12px rgba(0,0,0,0.2)"
        }}>
          <h3 style={{ marginBottom: "1rem" }}>Are you sure you want to permanently delete this transaction?</h3>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
            <button
              style={{ ...toolbarBtnStyle, background: "#d9534f" }}
              onClick={async () => {
                try {
                  const tx = showDeleteModal;

                  const plaidTxId = getPlaidTxIdFromTx(tx);

                  if (plaidTxId) {
                    await authedPost("/deleteTransaction", {
                      plaid_tx_id: plaidTxId,
                      source: "transactions_page_menu",
                    });
                  } else {
                    await deleteTransactionDoc(tx.id);
                  }

                  // Update UI
                  setTransactions((prev) => prev.filter((t) => t.id !== tx.id));
                  setShowDeleteModal(null);
                } catch (e) {
                  console.error("Delete failed:", e);
                  alert(e?.message || "Delete failed. Check console.");
                }
              }}
            >
              Delete Transaction
            </button>
            <button
              style={{ ...toolbarBtnStyle, background: "#f0f0f0", color: "#111" }}
              onClick={() => setShowDeleteModal(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showAddModal && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999
        }}>
          <div style={{
            width: "560px",
            maxWidth: "90vw",
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 12px 28px rgba(0,0,0,0.2)",
            padding: "20px"
          }}>
            <div style={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: 12 }}>
              Add Transaction
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div style={{ gridColumn: "1 / span 2" }}>
                <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Vendor</label>
                <input
                  type="text"
                  value={addVendor}
                  onChange={(e) => setAddVendor(e.target.value)}
                  placeholder="e.g., Grocery Store"
                  style={{ width: "100%" }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Date</label>
                <input
                  type="date"
                  value={addDate}
                  onChange={(e) => setAddDate(e.target.value)}
                  style={{ width: "100%" }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={addAmount}
                  onChange={(e) => setAddAmount(e.target.value)}
                  placeholder="e.g., 42.50"
                  style={{ width: "100%" }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Type</label>
                <select value={addType} onChange={(e) => setAddType(e.target.value)} style={{ width: "100%" }}>
                  <option value="expense">Expense</option>
                  <option value="income">Income</option>
                </select>
              </div>

              <div>
                <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Category</label>
                <select
                  value={addCategory}
                  onChange={(e) => {
                    setAddCategory(e.target.value);
                    setAddSubcategory("");
                  }}
                  style={{ width: "100%" }}
                >
                  <option value="">Select…</option>
                  {budgets.map((b, i) => {
                    const label = toLabel(b?.category ?? b);
                    return <option key={i} value={label}>{label}</option>;
                  })}
                </select>
              </div>

              <div>
                <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Subcategory</label>
                <select
                  value={addSubcategory}
                  onChange={(e) => setAddSubcategory(e.target.value)}
                  disabled={!addCategory}
                  style={{ width: "100%" }}
                >
                  <option value="">Select…</option>
                  {addSubcategoryOptions.map((s, i) => (
                    <option key={i} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: 18 }}>
              <button
                onClick={() => setShowAddModal(false)}
                style={{ ...toolbarBtnStyle, background: "#f0f0f0" }}
              >
                Cancel
              </button>
              <button
                onClick={saveManualTransaction}
                style={{ ...toolbarBtnStyle, background: "#00B074", color: "#fff", border: "none" }}
              >
                Save Transaction
              </button>
            </div>
          </div>
        </div>
      )}

      {isMobile && showMobileFilter && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 9999,
            display: "flex",
            alignItems: "flex-end"
          }}
          onClick={() => setShowMobileFilter(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxHeight: "80vh",
              background: "#fff",
              borderTopLeftRadius: 14,
              borderTopRightRadius: 14,
              boxShadow: "0 -8px 20px rgba(0,0,0,0.2)",
              padding: "14px"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>Filters</div>
              <button
                onClick={() => setShowMobileFilter(false)}
                aria-label="Close"
                title="Close"
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: "1.25rem",
                  lineHeight: 1,
                  padding: "4px 8px",
                  cursor: "pointer",
                  color: "#666"
                }}
              >
                ×
              </button>
            </div>

            {/* Scrollable content */}
            <div style={{ overflowY: "auto", maxHeight: "60vh", paddingRight: 4 }}>
              {/* Category */}
              <label style={{ display: "block", marginBottom: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Category</div>
                <select
                  value={filterCategory}
                  onChange={(e) => {
                    setFilterCategory(e.target.value);
                    setFilterSubcategory("");
                  }}
                  style={{ width: "100%", height: 36, borderRadius: 8, border: "1px solid #ccc", padding: "0 8px" }}
                >
                  <option value="">All Categories</option>
                  {budgets.map((b, i) => {
                    const label = toLabel(b?.category ?? b);
                    return <option key={i} value={label}>{label}</option>;
                  })}
                </select>
              </label>

              {/* Subcategory */}
              <label style={{ display: "block", marginBottom: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Subcategory</div>
                <select
                  value={filterSubcategory}
                  onChange={(e) => setFilterSubcategory(e.target.value)}
                  style={{ width: "100%", height: 36, borderRadius: 8, border: "1px solid #ccc", padding: "0 8px" }}
                >
                  <option value="">All Subcategories</option>
                  {(budgets.find((b) => toLabel(b.category) === filterCategory)?.subcategories || []).map((s, i) => {
                    const label = toLabel(typeof s === "string" ? s : (s?.name ?? s));
                    return <option key={i} value={label}>{label}</option>;
                  })}
                </select>
              </label>

              {/* Type */}
              <label style={{ display: "block", marginBottom: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Type</div>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  style={{ width: "100%", height: 36, borderRadius: 8, border: "1px solid #ccc", padding: "0 8px" }}
                >
                  <option value="">All Transactions</option>
                  <option value="income">Income only</option>
                  <option value="expense">Expense only</option>
                </select>
              </label>

              {/* Account */}
              <label style={{ display: "block", marginBottom: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Account</div>
                <select
                  value={filterAccount}
                  onChange={(e) => setFilterAccount(e.target.value)}
                  style={{ width: "100%", height: 36, borderRadius: 8, border: "1px solid #ccc", padding: "0 8px" }}
                >
                  <option value="">All Accounts</option>
                  {uniqueAccounts.map((name, i) => (
                    <option key={i} value={name}>{name}</option>
                  ))}
                </select>
              </label>

              {/* Status */}
              <label style={{ display: "block", marginBottom: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Status</div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{ width: "100%", height: 36, borderRadius: 8, border: "1px solid #ccc", padding: "0 8px" }}
                >
                  <option value="all">Matched & Suggested</option>
                  <option value="matched">Matched Only</option>
                  <option value="suggested">Suggested Only</option>
                </select>
              </label>

              {/* Date range */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))", // ⬅️ zero min to prevent overflow
                  gap: 8,
                  marginBottom: 10
                }}
              >
                <label>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>From</div>
                  <input
                    type="date"
                    aria-label="Start date"
                    value={filterDateRange.start}
                    onChange={(e) =>
                      setFilterDateRange({ ...filterDateRange, start: e.target.value })
                    }
                    style={{
                      width: "100%",
                      height: 32,
                      borderRadius: 8,
                      border: "1px solid #ccc",
                      padding: "0 4px",
                      boxSizing: "border-box",
                      minWidth: 0,
                      fontSize: "0.85rem",
                      WebkitAppearance: "none",
                      MozAppearance: "textfield"
                    }}
                  />
                </label>
                <label>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>To</div>
                  <input
                    type="date"
                    aria-label="End date"
                    value={filterDateRange.end}
                    onChange={(e) =>
                      setFilterDateRange({ ...filterDateRange, end: e.target.value })
                    }
                    style={{
                      width: "100%",
                      height: 32,
                      borderRadius: 8,
                      border: "1px solid #ccc",
                      padding: "0 4px",
                      boxSizing: "border-box",
                      minWidth: 0,
                      fontSize: "0.85rem",
                      WebkitAppearance: "none",
                      MozAppearance: "textfield"
                    }}
                  />
                </label>
              </div>

              {/* Amount */}
              <label style={{ display: "block", marginBottom: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Exact Amount</div>
                <input
                  type="number"
                  placeholder="e.g., -42.50"
                  value={filterAmount}
                  onChange={(e) => setFilterAmount(e.target.value)}
                  style={{ width: "100%", height: 36, borderRadius: 8, border: "1px solid #ccc", padding: "0 8px" }}
                />
              </label>

              {/* Toggles */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={filterConfirmOnly}
                    onChange={() => setFilterConfirmOnly(!filterConfirmOnly)}
                  />
                  <span>Needs Confirmation</span>
                </label>

                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={filterNeedsAttention}
                    onChange={() => setFilterNeedsAttention(!filterNeedsAttention)}
                  />
                  <span>Needs Attention</span>
                </label>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button
                onClick={() => {
                  setFilterCategory("");
                  setFilterSubcategory("");
                  setFilterAccount("");
                  setFilterType("");
                  setFilterStatus("all");
                  setFilterDateRange({ start: "", end: "" });
                  setFilterAmount("");
                  setFilterConfirmOnly(false);
                  setFilterNeedsAttention(false);
                }}
                style={{
                  position: "relative",
                  height: 40,
                  padding: "0 14px",
                  borderRadius: 12,
                  border: "1px solid #ccc",
                  background: "#f0f0f0",
                  color: "#111",
                  fontWeight: 600
                }}
              >
                Clear
                {activeFilterCount > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      minWidth: 18,
                      height: 18,
                      padding: "0 5px",
                      borderRadius: 999,
                      background: TRELLI_YELLOW,
                      color: "#111",
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: "18px",
                      textAlign: "center",
                      boxShadow: "0 0 0 1px #fff"
                    }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setShowMobileFilter(false)}
                style={{
                  height: 40,
                  padding: "0 14px",
                  borderRadius: 12,
                  border: "none",
                  background: TRELLI_GREEN,
                  color: "#fff",
                  fontWeight: 700
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    </div>
    </>
  );
}